import { useMemo, useState } from 'react';
import { presentationFrames, redo, startPresentation, undo, zoomToFit, type ActionContext } from '../actions/actions';
import { useCallButtonState } from '../call/CallWindow';
import { useBoardMeta, useConnection, useDocVersion, usePeers, useUndoState, useVoteSession } from '../collab/hooks';
import { exportBoard, type ExportFormat } from '../export/exportBoard';
import { exportFramesToPdf, exportFramesToPptx, printFrames } from '../export/exportFrames';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { ApiError, api, type BoardRoleName } from '../lib/api';
import { inviteUrl } from '../lib/authApi';
import { useEditor } from '../state/store';
import { TemplateGallery } from '../templates/TemplateGallery';
import { BoardAccessPanel } from '../dashboard/BoardAccessPanel';
import { IconButton, PanelDivider, Popover, Segmented } from './controls';
import { BrandLogo } from './Brand';
import { Icon } from './icons';
import { ThemeToggle } from '../theme/ThemeToggle';
import { HistoryPanel } from './HistoryPanel';
import { LayersPanel } from './LayersPanel';
import { TimerPanel, TimerWidget } from './TimerWidget';
import { VotingPanel } from './VotingPanel';

/** Matches the mobile block in styles.css, where the rest of the chrome moves. */
const COMPACT = '(max-width: 720px)';

export interface TopBarProps {
  ctx: ActionContext;
  /** Leaves the board and returns to the dashboard. */
  onExit?: () => void;
  orgId: string;
  boardName: string;
}

export function TopBar({ ctx, onExit, orgId, boardName }: TopBarProps) {
  const meta = useBoardMeta(ctx.doc);
  const connection = useConnection(ctx.doc);
  const peers = usePeers(ctx.doc);
  const { canUndo, canRedo } = useUndoState(ctx.doc);
  const compact = useMediaQuery(COMPACT);
  const [renaming, setRenaming] = useState(false);
  const docVersion = useDocVersion(ctx.doc);
  const frameCount = useMemo(() => presentationFrames(ctx.doc).length, [ctx.doc, docVersion]);
  const [showTemplates, setShowTemplates] = useState(false);
  const voteSession = useVoteSession(ctx.doc);
  const canEdit = ctx.doc.canEdit();
  // Prefer the live Yjs title once it is set; until then (or for viewers who
  // cannot write the seed) fall back to the name from GET /boards/:id.
  const displayName =
    meta.name && meta.name !== 'Без названия'
      ? meta.name
      : boardName || meta.name;

  return (
    <header className="topbar">
      <div className="topbar-left">
        {onExit && (
          <button
            type="button"
            className="icon-btn"
            onClick={onExit}
            aria-label="Вернуться к доскам"
            data-tip="Все доски"
          >
            <Icon name="arrowLeft" size={17} />
          </button>
        )}
        <BrandLogo />

        {renaming && canEdit ? (
          <input
            className="board-name-input"
            defaultValue={displayName}
            autoFocus
            onBlur={(e) => {
              const name = e.target.value.trim() || 'Без названия';
              ctx.doc.setMeta({ name });
              void api.updateBoard(ctx.doc.boardId, { name }).catch(() => undefined);
              setRenaming(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setRenaming(false);
              e.stopPropagation();
            }}
          />
        ) : (
          <button
            type="button"
            className="board-name"
            disabled={!canEdit}
            onClick={() => canEdit && setRenaming(true)}
          >
            {displayName}
          </button>
        )}

        {!canEdit && (
          <span className={`role-badge role-${ctx.doc.role}`}>
            {ctx.doc.role === 'viewer' ? 'Только просмотр' : 'Комментирование'}
          </span>
        )}

        <ConnectionBadge status={connection.status} />
      </div>

      {/*
        Rebuilt, not restyled, on a narrow screen: eight controls do not fit a
        phone's top bar, and hiding the row — which is what this used to do —
        left undo, layers, history, the timer and voting with no route to them
        at all. Undo and redo keep their place; the rest becomes one menu.
      */}
      <div className="topbar-center">
        {canEdit && (
          <>
            <IconButton icon="undo" label="Отменить (Ctrl Z)" disabled={!canUndo} onClick={() => undo(ctx)} />
            <IconButton icon="redo" label="Повторить (Ctrl Shift Z)" disabled={!canRedo} onClick={() => redo(ctx)} />
            {!compact && <PanelDivider />}
          </>
        )}

        {compact ? (
          <Popover
            title="Инструменты доски"
            trigger={() => (
              <>
                <Icon name="more" size={18} />
                {/* A vote running behind the menu would otherwise be invisible. */}
                {voteSession.active && <span className="vote-active-dot" aria-hidden="true" />}
              </>
            )}
          >
            {(close) => <BoardToolsMenu ctx={ctx} onPick={close} />}
          </Popover>
        ) : (
          <>
            <Popover title="Сетка" trigger={() => <Icon name="grid" size={16} />}>
              {() => <GridPanel ctx={ctx} />}
            </Popover>
            <IconButton icon="fit" label="Вписать в экран (Shift 1)" onClick={() => zoomToFit(ctx)} />
            <Popover title="Слои и поиск" trigger={() => <Icon name="layers" size={16} />}>
              {() => <LayersPanel ctx={ctx} />}
            </Popover>
            {canEdit && (
              <Popover title="История версий" trigger={() => <Icon name="history" size={16} />}>
                {() => <HistoryPanel doc={ctx.doc} />}
              </Popover>
            )}
            <PanelDivider />
            <TimerWidget doc={ctx.doc} />
            <Popover
              title="Голосование"
              trigger={() => (
                <>
                  <Icon name="vote" size={16} />
                  {voteSession.active && <span className="vote-active-dot" aria-hidden="true" />}
                </>
              )}
            >
              {() => <VotingPanel ctx={ctx} />}
            </Popover>
          </>
        )}
      </div>

      <div className="topbar-right">
        <PresenceStack peers={peers} self={ctx.identity} />

        <ThemeToggle />

        <CallButton />

        {canEdit && (
          <button
            type="button"
            className="icon-btn"
            onClick={() => setShowTemplates(true)}
            aria-label="Шаблоны"
            data-tip="Шаблоны"
          >
            <Icon name="template" size={16} />
          </button>
        )}

        <button
          type="button"
          className="icon-btn"
          disabled={frameCount === 0}
          onClick={() => startPresentation(ctx.doc)}
          aria-label="Показ презентации по фреймам"
          data-tip={frameCount === 0 ? 'Нужен хотя бы один фрейм' : 'Показ по фреймам'}
        >
          <Icon name="play" size={16} />
        </button>

        {showTemplates && canEdit && <TemplateGallery ctx={ctx} onClose={() => setShowTemplates(false)} />}

        <Popover
          align="right"
          title="Экспорт"
          trigger={() => (
            <>
              <Icon name="download" size={16} />
              <span className="btn-label">Экспорт</span>
            </>
          )}
        >
          {(close) => (
            <div className="popover-stack">
              {(
                [
                  ['png', 'Изображение PNG'],
                  ['svg', 'Векторный SVG'],
                  ['json', 'Доска в JSON'],
                ] as [ExportFormat, string][]
              ).map(([format, label]) => (
                <button
                  key={format}
                  type="button"
                  className="menu-item"
                  onClick={async () => {
                    close();
                    await exportBoard(ctx.doc, format, { selectionOnly: false });
                  }}
                >
                  <Icon name="download" size={16} /> {label}
                </button>
              ))}
              <PanelDivider />
              <button
                type="button"
                className="menu-item"
                onClick={async () => {
                  close();
                  await exportBoard(ctx.doc, 'png', { selectionOnly: true });
                }}
              >
                <Icon name="download" size={16} /> Выделенное как PNG
              </button>
              <PanelDivider />
              <button
                type="button"
                className="menu-item"
                disabled={frameCount === 0}
                data-tip={frameCount === 0 ? 'Нужен хотя бы один фрейм' : undefined}
                onClick={async () => {
                  close();
                  const ok = await exportFramesToPdf(ctx.doc);
                  if (!ok) window.alert('На доске нет фреймов для экспорта.');
                }}
              >
                <Icon name="download" size={16} /> PDF по фреймам
              </button>
              <button
                type="button"
                className="menu-item"
                disabled={frameCount === 0}
                data-tip={frameCount === 0 ? 'Нужен хотя бы один фрейм' : undefined}
                onClick={async () => {
                  close();
                  const ok = await exportFramesToPptx(ctx.doc);
                  if (!ok) window.alert('На доске нет фреймов для экспорта.');
                }}
              >
                <Icon name="download" size={16} /> PPTX по фреймам
              </button>
              <button
                type="button"
                className="menu-item"
                disabled={frameCount === 0}
                data-tip={frameCount === 0 ? 'Нужен хотя бы один фрейм' : undefined}
                onClick={async () => {
                  close();
                  const ok = await printFrames(ctx.doc);
                  if (!ok) window.alert('На доске нет фреймов для печати.');
                }}
              >
                <Icon name="print" size={16} /> Печать по фреймам
              </button>
            </div>
          )}
        </Popover>

        <BoardAccessPanel
          boardId={ctx.doc.boardId}
          boardName={boardName || meta.name}
          orgId={orgId}
          compact
        />

        <SharePopover boardId={ctx.doc.boardId} />
      </div>
    </header>
  );
}

/** Grid visibility and style, mountable on the bar or inside the compact menu. */
function GridPanel({ ctx }: { ctx: ActionContext }) {
  const meta = useBoardMeta(ctx.doc);
  const showGrid = useEditor((s) => s.showGrid);
  const canEdit = ctx.doc.canEdit();

  return (
    <div className="popover-stack">
      <button
        type="button"
        className="menu-item"
        onClick={() => useEditor.getState().setUi({ showGrid: !showGrid })}
      >
        <Icon name={showGrid ? 'check' : 'close'} size={16} /> Показывать сетку
      </button>
      {canEdit && showGrid && (
        <>
          <PanelDivider />
          <div className="popover-label">Стиль</div>
          <Segmented
            value={meta.grid === 'lines' ? 'lines' : 'dots'}
            options={[
              { value: 'dots', label: 'Точки' },
              { value: 'lines', label: 'Клетка' },
            ]}
            onChange={(grid) => ctx.doc.setMeta({ grid })}
          />
        </>
      )}
    </div>
  );
}

/** Which of the board panels the compact menu is showing, if any. */
type ToolsView = 'menu' | 'grid' | 'layers' | 'history' | 'timer' | 'vote';

/**
 * The board panels that have no room on a phone's top bar, behind one button.
 *
 * They are sub-views of a single popover rather than popovers of their own: a
 * panel is portalled to the body, so a nested popover would not be a DOM
 * descendant of the panel that opened it, would be read as a click outside,
 * and would close the menu underneath it on the first tap.
 */
function BoardToolsMenu({ ctx, onPick }: { ctx: ActionContext; onPick: () => void }) {
  const [view, setView] = useState<ToolsView>('menu');
  const canEdit = ctx.doc.canEdit();

  if (view !== 'menu') {
    return (
      <div className="popover-stack">
        <button type="button" className="menu-item" onClick={() => setView('menu')}>
          <Icon name="arrowLeft" size={16} /> Назад
        </button>
        <PanelDivider />
        {view === 'grid' && <GridPanel ctx={ctx} />}
        {view === 'layers' && <LayersPanel ctx={ctx} />}
        {view === 'history' && <HistoryPanel doc={ctx.doc} />}
        {view === 'timer' && <TimerPanel doc={ctx.doc} />}
        {view === 'vote' && <VotingPanel ctx={ctx} />}
      </div>
    );
  }

  return (
    <div className="popover-stack">
      <button type="button" className="menu-item" onClick={() => setView('grid')}>
        <Icon name="grid" size={16} /> Сетка
      </button>
      {/* Acts immediately, so it closes the menu it was picked from. */}
      <button
        type="button"
        className="menu-item"
        onClick={() => {
          zoomToFit(ctx);
          onPick();
        }}
      >
        <Icon name="fit" size={16} /> Вписать в экран
      </button>
      <button type="button" className="menu-item" onClick={() => setView('layers')}>
        <Icon name="layers" size={16} /> Слои и поиск
      </button>
      {canEdit && (
        <button type="button" className="menu-item" onClick={() => setView('history')}>
          <Icon name="history" size={16} /> История версий
        </button>
      )}
      <button type="button" className="menu-item" onClick={() => setView('timer')}>
        <Icon name="timer" size={16} /> Таймер
      </button>
      <button type="button" className="menu-item" onClick={() => setView('vote')}>
        <Icon name="vote" size={16} /> Голосование
      </button>
    </div>
  );
}

const SHARE_LINKS: { role: BoardRoleName; label: string; hint: string }[] = [
  { role: 'editor', label: 'Редактор', hint: 'Полный доступ: рисовать, двигать, удалять' },
  { role: 'commenter', label: 'Комментатор', hint: 'Может оставлять комментарии и голосовать' },
  { role: 'viewer', label: 'Только просмотр', hint: 'Смотрит доску и презентацию, не меняет её' },
];

/**
 * Issues a real invite token and copies `/?invite=<token>`.
 *
 * The old share menu pasted `?b=` + `?role=` into the clipboard. That URL is
 * no longer permission — recipients hit "нет доступа" — so each click now
 * creates a stored board grant the landing page can redeem.
 */
function SharePopover({ boardId }: { boardId: string }) {
  const [copiedRole, setCopiedRole] = useState<string | null>(null);
  const [busyRole, setBusyRole] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUrl, setLastUrl] = useState<string | null>(null);

  const copy = async (role: BoardRoleName) => {
    setBusyRole(role);
    setError(null);
    try {
      const invite = await api.createBoardInvite(boardId, role);
      const url = inviteUrl(invite.token);
      setLastUrl(url);
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Clipboard blocked: the link stays visible below to copy by hand.
      }
      setCopiedRole(role);
      setTimeout(() => setCopiedRole((r) => (r === role ? null : r)), 1600);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 403
          ? 'Приглашать может только владелец доски или администратор организации.'
          : 'Не удалось создать ссылку-приглашение.',
      );
    } finally {
      setBusyRole(null);
    }
  };

  return (
    <Popover
      align="right"
      title="Поделиться доской"
      trigger={() => (
        <>
          <Icon name="share" size={16} />
          <span className="btn-label">Поделиться</span>
        </>
      )}
    >
      {() => (
        <div className="popover-stack share-popover">
          <p className="popover-hint">
            Создаётся ссылка-приглашение: получатель входит или регистрируется и сразу получает доступ.
          </p>
          {SHARE_LINKS.map(({ role, label, hint }) => (
            <button
              key={role}
              type="button"
              className="menu-item share-link-item"
              disabled={busyRole !== null}
              onClick={() => void copy(role)}
            >
              <Icon name={copiedRole === role ? 'check' : 'link'} size={16} />
              <span className="share-link-text">
                <strong>{busyRole === role ? 'Создаём…' : label}</strong>
                <small>{hint}</small>
              </span>
            </button>
          ))}
          {error && <div className="share-error">{error}</div>}
          {lastUrl && (
            <input
              className="tag-input"
              readOnly
              value={lastUrl}
              aria-label="Ссылка-приглашение"
              onFocus={(e) => e.currentTarget.select()}
            />
          )}
        </div>
      )}
    </Popover>
  );
}

function ConnectionBadge({ status }: { status: string }) {
  const label =
    status === 'connected'
      ? 'Онлайн'
      : status === 'connecting'
        ? 'Подключение'
        : status === 'offline'
          ? 'Нет сети'
          : status === 'denied'
            ? 'Нет доступа'
            : 'Переподключение';
  const hint =
    status === 'connected'
      ? 'Совместная работа'
      : status === 'denied'
        ? 'Доступ к доске закрыт. Изменения больше не отправляются'
        : 'Изменения сохраняются на этом устройстве и синхронизируются, когда сеть вернётся';
  return (
    <span className={`conn-badge conn-${status}`} title={`${label}: ${hint}`}>
      <span className="conn-dot" />
      {/* Wrapped so a phone can drop the word and keep the dot. */}
      <span className="conn-label">{label}</span>
    </span>
  );
}

function PresenceStack({
  peers,
  self,
}: {
  peers: { userId: string; name: string; color: string }[];
  self: { name: string; color: string };
}) {
  const following = useEditor((s) => s.followingUserId);
  const shown = peers.slice(0, 4);
  const overflow = peers.length - shown.length;

  return (
    <div className="presence-stack" aria-label={`${peers.length + 1} участников на доске`}>
      <span className="avatar" data-tip={`${self.name} · вы`} style={{ background: self.color }}>
        {initials(self.name)}
      </span>
      {shown.map((peer) => {
        const active = following === peer.userId;
        return (
          <button
            key={peer.userId}
            type="button"
            className={`avatar avatar-btn ${active ? 'is-following' : ''}`}
            style={{ background: peer.color }}
            data-tip={
              active ? `${peer.name} · отменить слежение` : `${peer.name} · следить за камерой`
            }
            aria-label={
              active ? `Не следить за ${peer.name}` : `Следить за камерой ${peer.name}`
            }
            aria-pressed={active}
            onClick={() =>
              useEditor.getState().setUi({ followingUserId: active ? null : peer.userId })
            }
          >
            {initials(peer.name)}
          </button>
        );
      })}
      {overflow > 0 && <span className="avatar avatar-more">+{overflow}</span>}
    </div>
  );
}

/** Starts or leaves the board's call. The dock itself appears once connected. */
function CallButton() {
  const { inCall, configured, onClick } = useCallButtonState();
  return (
    <button
      type="button"
      className={`icon-btn ${inCall ? 'is-active' : ''}`}
      onClick={onClick}
      aria-pressed={inCall}
      aria-label={inCall ? 'Покинуть звонок' : 'Позвонить'}
      data-tip={
        inCall ? 'Покинуть звонок' : configured ? 'Позвонить' : 'Позвонить (LiveKit не настроен)'
      }
    >
      <Icon name="phone" size={16} />
    </button>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
